"use client"

import type React from "react"
import { useState } from "react"
import { Sun, Menu, X } from "lucide-react"

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  return (
    <header className="w-full bg-white">
      {/* Top Bar */}
      <div className="bg-gray-100 px-2 sm:px-4 py-1 sm:py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs sm:text-sm">
          <div className="flex items-center space-x-1 sm:space-x-2 text-gray-700">
            <span className="hidden sm:inline">Government of Telangana</span>
            <span className="sm:hidden">Govt of Telangana</span>
            <span className="text-gray-400 hidden sm:inline">|</span>
            <span className="hidden md:inline">Ministry of Tourism & Culture</span>
            <span className="md:hidden">Tourism</span>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button className="text-gray-700 hover:text-blue-600 transition-colors text-xs sm:text-sm">हिंदी</button>
            <span className="text-gray-400">|</span>
            <button className="text-gray-700 hover:text-blue-600 transition-colors text-xs sm:text-sm">తెలుగు</button>
            <span className="text-gray-400 hidden sm:inline">|</span>
            <button className="text-gray-700 hover:text-blue-600 transition-colors hidden sm:inline text-xs sm:text-sm">
              Accessibility
            </button>
            <button className="h-6 w-6 sm:h-8 sm:w-8 p-0 bg-transparent border border-gray-300 rounded hover:bg-gray-50">
              <Sun className="h-3 w-3 sm:h-4 sm:w-4 mx-auto" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-white px-2 sm:px-4 py-3 sm:py-6 border-b">
        <div className="max-w-7xl mx-auto">
          {/* Mobile Layout */}
          <div className="block lg:hidden">
            {/* Logo centered on mobile */}
            <div className="flex justify-center mb-4">
              <img
                src="/images/telangana-logo.png"
                alt="Telangana Tourism Logo"
                className="h-12 sm:h-14 w-auto object-contain"
              />
            </div>

            {/* Officials row on mobile */}
            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              {/* Chief Minister */}
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  <img
                    src="/images/revanth-reddy.png"
                    alt="Sri. A. Revanth Reddy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-xs sm:text-sm leading-tight">
                    Sri. A. Revanth Reddy
                  </h3>
                  <p className="text-xs text-gray-600 leading-tight">Hon'ble Chief Minister</p>
                </div>
              </div>

              {/* Tourism Minister */}
              <div className="flex items-center justify-end space-x-2 sm:space-x-3">
                <div className="text-right min-w-0">
                  <h3 className="font-semibold text-gray-900 text-xs sm:text-sm leading-tight">
                    Sri. Jupally Krishna Rao
                  </h3>
                  <p className="text-xs text-gray-600 leading-tight">Tourism Dept</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  <img
                    src="/images/jupally-krishna-rao.png"
                    alt="Sri. Jupally Krishna Rao"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-6 items-center">
            {/* Left - Chief Minister */}
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                <img
                  src="/images/revanth-reddy.png"
                  alt="Sri. A. Revanth Reddy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Sri. A. Revanth Reddy</h3>
                <p className="text-sm text-gray-600">Honourable Chief Minister</p>
              </div>
            </div>

            {/* Center - Logo */}
            <div className="flex justify-center">
              <img
                src="/images/telangana-logo.png"
                alt="Telangana Tourism Logo"
                className="h-16 sm:h-20 w-auto object-contain"
              />
            </div>

            {/* Right - Tourism Minister */}
            <div className="flex items-center justify-end space-x-4">
              <div className="text-right">
                <h3 className="font-semibold text-gray-900">Sri. Jupally Krishna Rao</h3>
                <p className="text-sm text-gray-600">Dept of Tourism and culture</p>
              </div>
              <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                <img
                  src="/images/jupally-krishna-rao.png"
                  alt="Sri. Jupally Krishna Rao"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="bg-blue-600 text-white relative">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-between h-14">
            <div className="flex space-x-8">
              <a href="#" className="hover:text-blue-200 transition-colors">
                Home
              </a>
              <a href="#" className="hover:text-blue-200 transition-colors">
                Destinations
              </a>
              <a href="#" className="hover:text-blue-200 transition-colors">
                Experiences
              </a>
              <a href="#" className="hover:text-blue-200 transition-colors">
                Stay & Dine
              </a>
              <a href="#" className="hover:text-blue-200 transition-colors">
                Culture & Heritage
              </a>
              <a href="#" className="hover:text-blue-200 transition-colors">
                Travel Guide
              </a>
              <a href="#" className="hover:text-blue-200 transition-colors">
                Events
              </a>
              <a href="#" className="hover:text-blue-200 transition-colors">
                Manage Booking
              </a>
              <a href="#" className="hover:text-blue-200 transition-colors">
                Login
              </a>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between h-12">
              <span className="font-semibold text-sm">Menu</span>
              <button
                onClick={toggleMobileMenu}
                className="text-white hover:text-blue-200 hover:bg-blue-700 p-2 rounded"
              >
                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>

            {/* Mobile Menu Dropdown */}
            {isMobileMenuOpen && (
              <div className="absolute top-full left-0 right-0 bg-blue-600 border-t border-blue-500 z-50 shadow-lg">
                <div className="px-2 py-2 space-y-1">
                  <a href="#" className="block px-3 py-2 text-sm hover:bg-blue-700 rounded transition-colors">
                    Home
                  </a>
                  <a href="#" className="block px-3 py-2 text-sm hover:bg-blue-700 rounded transition-colors">
                    Destinations
                  </a>
                  <a href="#" className="block px-3 py-2 text-sm hover:bg-blue-700 rounded transition-colors">
                    Experiences
                  </a>
                  <a href="#" className="block px-3 py-2 text-sm hover:bg-blue-700 rounded transition-colors">
                    Stay & Dine
                  </a>
                  <a href="#" className="block px-3 py-2 text-sm hover:bg-blue-700 rounded transition-colors">
                    Culture & Heritage
                  </a>
                  <a href="#" className="block px-3 py-2 text-sm hover:bg-blue-700 rounded transition-colors">
                    Travel Guide
                  </a>
                  <a href="#" className="block px-3 py-2 text-sm hover:bg-blue-700 rounded transition-colors">
                    Events
                  </a>
                  <a href="#" className="block px-3 py-2 text-sm hover:bg-blue-700 rounded transition-colors">
                    Manage Booking
                  </a>
                  <a href="#" className="block px-3 py-2 text-sm hover:bg-blue-700 rounded transition-colors">
                    Login
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-blue-600 text-white py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 leading-tight">
            Plan Your Trip
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-blue-100 leading-relaxed">
            Find destinations, experiences, and create your perfect journey
          </p>
        </div>
      </div>
    </header>
  )
}

export default Header
